"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calculator, Scale, Stethoscope, Zap, Wrench, Building2, ShoppingBag, Briefcase, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const sectorList = [{
  title: "Accountants & Financial Services",
  description: "Target high-value audit, tax, and advisory clients with structured search strategies that build trust and sector authority.",
  icon: Calculator,
  category: "Professional Services",
  benefit: "High-intent lead capture"
}, {
  title: "Solicitors & Law Firms",
  description: "Capture competitive legal search traffic locally and nationally to generate qualified instructions directly for your fee earners.",
  icon: Scale,
  category: "Legal Practice",
  benefit: "Commercial lead growth"
}, {
  title: "Dentists & Private Healthcare",
  description: "Drive patient enquiries for high-margin cosmetic and restorative treatments with Google Maps optimization and local authority.",
  icon: Stethoscope,
  category: "Healthcare",
  benefit: "Patient acquisition"
}, {
  title: "Electricians & Contractors",
  description: "Dominate local trade searches and secure lucrative commercial maintenance contracts across your primary coverage regions.",
  icon: Zap,
  category: "Trades & Services",
  benefit: "Regional visibility"
}, {
  title: "Plumbers & HVAC Specialists",
  description: "Rank at the top for emergency response searches and planned installation enquiries in every town you serve.",
  icon: Wrench,
  category: "Trades & Services",
  benefit: "Local pack domination"
}, {
  title: "Estate Agents & Property",
  description: "Outrank generic portal competitors for local property valuation searches and win more direct seller instructions.",
  icon: Building2,
  category: "Property",
  benefit: "Direct instructions"
}, {
  title: "E-Commerce & Online Retailers",
  description: "Scale organic revenue with clean technical SEO, structured product schema, and high-converting category page optimization.",
  icon: ShoppingBag,
  category: "E-Commerce",
  benefit: "Revenue scale"
}, {
  title: "B2B & Technology Providers",
  description: "Attract decision-makers with intent-focused keyword mapping, technical content hubs, and sustainable organic pipeline growth.",
  icon: Briefcase,
  category: "B2B & Tech",
  benefit: "Qualified pipeline"
}];
export default function Sectors() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="industrysectorsgrid" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground rounded-full">
            Sectors & Industries Served
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground mb-6">
            Bespoke SEO Strategy Built for Your Specific Industry
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Every market has unique search behaviors and competitive dynamics. You work directly with me to craft a tailored organic search strategy aligned with how your ideal clients actually search.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-16">
          {sectorList.map((sector, i) => {
          const IconComponent = sector.icon;
          return <motion.div key={sector.title} variants={itemVariants} data-index={i}>
                <Card className="h-full bg-card text-card-foreground border border-border shadow-sm hover:shadow-md transition-[color,background-color,border-color,box-shadow] duration-300 rounded-xl flex flex-col justify-between group">
                  <CardHeader className="p-6 pb-3">
                    <div className="flex items-center justify-between mb-4">
                      <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <IconComponent className="size-6 shrink-0" />
                      </div>
                      <Badge variant="outline" className="text-xs text-muted-foreground border-border rounded-md">
                        {sector.category}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold font-default text-foreground group-hover:text-primary transition-colors duration-200">
                      {sector.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-6 pt-0 flex-1 flex flex-col justify-between gap-4">
                    <CardDescription className="text-sm text-muted-foreground font-default leading-relaxed">
                      {sector.description}
                    </CardDescription>

                    <div className="pt-3 border-t border-border/60 flex items-center gap-2 text-xs font-medium text-primary">
                      <CheckCircle2 className="size-4 shrink-0 text-primary" />
                      <span>{sector.benefit}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="bg-muted/50 border border-border rounded-2xl p-8 lg:p-12 text-center max-w-4xl mx-auto flex flex-col items-center gap-6">
          <div className="max-w-2xl">
            <h3 className="text-2xl sm:text-3xl font-semibold font-default text-foreground mb-3">
              Don’t See Your Specific Industry Listed?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground font-default leading-relaxed">
              I apply robust technical SEO frameworks, intent-focused content mapping, and authoritative digital PR across all competitive B2B and consumer sectors. Let’s discuss your unique goals.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-8 py-6 font-medium shadow-md transition-colors">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border text-foreground hover:bg-accent hover:text-accent-foreground rounded-lg px-8 py-6 font-medium transition-colors">
              <Link to="/CaseStudiesPage">Explore Case Studies</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}